import { ScrollView, View } from "@-/com.npmjs.react-native";
import { useImperativeHandle, useRef, useState, type ComponentPropsWithRef } from "react";
import { ScrollToBottomButton } from "@-/dev.improve.haniverse.components.ui.scroll-to-bottom-button";
import { useCardScrollOptIn } from "@-/dev.improve.haniverse.lib.cards.review-swipe";

/**
 * Preserve the native scroll view after opting in, including at either scroll boundary.
 *
 * Pass `showsScrollToBottomButton={false}` when the content collapses whatever would overflow and
 * offers its own in-flow "Show More" (it then calls `enableScroll` itself on expansion).
 */
export function FlashcardScrollView({
  onContentSizeChange,
  onLayout,
  onScroll,
  ref,
  scrollEnabled = true,
  showsHorizontalScrollIndicator = true,
  showsScrollToBottomButton = true,
  showsVerticalScrollIndicator = true,
  ...props
}: ComponentPropsWithRef<typeof ScrollView> & { showsScrollToBottomButton?: boolean }) {
  const { enableScroll, isCard, isScrollEnabled } = useCardScrollOptIn();
  const scrollRef = useRef<ScrollView>(null);
  useImperativeHandle(ref, () => {
    const scroll = scrollRef.current;
    if (scroll === null) {
      throw new Error("Card scroll view must be mounted before its handle is read");
    }
    return scroll;
  }, []);
  const [viewportHeight, setViewportHeight] = useState(0);
  const [contentHeight, setContentHeight] = useState(0);
  const [offset, setOffset] = useState(0);
  const [didShowMore, setDidShowMore] = useState(false);
  const hasOverflow = viewportHeight > 0 && contentHeight > viewportHeight + 1;
  return (
    <View className="min-h-0 flex-1">
      <ScrollView
        {...props}
        bounces={scrollEnabled && isScrollEnabled && hasOverflow}
        onContentSizeChange={(width, height) => {
          setContentHeight(height);
          onContentSizeChange?.(width, height);
        }}
        onLayout={(event) => {
          setViewportHeight(event.nativeEvent.layout.height);
          onLayout?.(event);
        }}
        onScroll={(event) => {
          setOffset(event.nativeEvent.contentOffset.y);
          onScroll?.(event);
        }}
        ref={scrollRef}
        scrollEnabled={scrollEnabled && isScrollEnabled && hasOverflow}
        showsHorizontalScrollIndicator={
          showsHorizontalScrollIndicator && scrollEnabled && isScrollEnabled && hasOverflow
        }
        showsVerticalScrollIndicator={
          showsVerticalScrollIndicator && scrollEnabled && isScrollEnabled && hasOverflow
        }
      />
      {isCard &&
      showsScrollToBottomButton &&
      !didShowMore &&
      hasOverflow &&
      (!isScrollEnabled || contentHeight - viewportHeight - offset > 24) ? (
        <ScrollToBottomButton
          onPress={() => {
            setDidShowMore(true);
            enableScroll();
            scrollRef.current?.scrollTo({
              animated: true,
              y: Math.min(contentHeight - viewportHeight, offset + viewportHeight * 0.75),
            });
          }}
        />
      ) : null}
    </View>
  );
}
